import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { spacing } from '../../constants';
import { normalizeRole } from '../../utils/assignmentRules';
import RegionCard from './RegionCard';

/**
 * RegionCardsGrid Component
 * Grid of region cards with status cards
 */
const RegionCardsGrid = ({
  visibleRegionOptions,
  regionStatusSummary,
  edUsersByRegion,
  selectedRegionCard,
  selectedStatusCard,
  onRegionCardClick,
  onStatusCardClick,
  tableContainerRef,
}) => {
  // Get current user and filter regions for ED role
  const filteredRegionOptions = useMemo(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return visibleRegionOptions;

      const user = JSON.parse(storedUser);
      const userRole = normalizeRole(user?.role);
      const userRegion = (user?.region || '').trim();

      // For ED role, only show their assigned region
      if (userRole === 'ED' && userRegion) {
        // regionStatusSummary is keyed by whatever region the backend actually
        // scoped the counts to. When a user holds multiple posts across
        // regions (see PostSelector), that's the *active post's* region, not
        // necessarily the account's static home region below - prefer it
        // when the backend gave us exactly one (post-scoped) region back.
        const scopedRegionKeys = Object.keys(regionStatusSummary || {});
        const activeRegion = scopedRegionKeys.length === 1 ? scopedRegionKeys[0] : userRegion;
        return visibleRegionOptions.filter(
          (regionOption) => regionOption.value === activeRegion
        );
      }

      // For other roles, show all regions
      return visibleRegionOptions;
    } catch (error) {
      // console.error('Failed to parse user from localStorage:', error);
      return visibleRegionOptions;
    }
  }, [visibleRegionOptions, regionStatusSummary]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: filteredRegionOptions.length === 1 
          ? '1fr' // Full width when only one region card
          : {
              xs: '1fr', // 1 column on mobile
              sm: 'repeat(2, 1fr)', // 2 columns on small tablets
              md: 'repeat(3, 1fr)', // 3 columns on medium and larger screens
              lg: 'repeat(3, 1fr)', // 3 columns on large screens
              xl: 'repeat(3, 1fr)', // 3 columns on extra large screens
            },
        gap: spacing.md,
        mb: spacing.md,
        width: '100%',
        '& > *': {
          width: '100%',
          minWidth: 0, // Prevent overflow
        },
      }}
    >
      {filteredRegionOptions.map((regionOption) => {
        const regionCode = regionOption.value;
        const regionStats = regionStatusSummary[regionCode] || {
          raised: 0,
          closed: 0,
          delegated: 0,
          escalated: 0,
          total: 0,
          totalProjects: 0,
          totalOpenQueries: 0,
        };
        const isSelected = selectedRegionCard === regionCode;
        
        // Get ED user for this region
        const edUsersForRegion = edUsersByRegion[regionCode] || [];
        const edUser = edUsersForRegion.length > 0 ? edUsersForRegion[0] : null;
        
        return (
          <RegionCard
            key={regionCode}
            regionOption={regionOption}
            regionStats={regionStats}
            edUser={edUser}
            isSelected={isSelected}
            selectedStatusCard={selectedStatusCard}
            onClick={() => onRegionCardClick(regionCode)}
            onStatusCardClick={onStatusCardClick}
            tableContainerRef={tableContainerRef}
          />
        );
      })}
    </Box>
  );
};

export default RegionCardsGrid;
